/**
 * BookingWeekStrip.tsx
 * A tiny Sa–Fr week strip with the day(s) a booking covers filled in (Option A) — so a
 * "1d · Jul 2" reads at a glance as "Thursday", and a multi-day booking shows its span.
 * Used in all three task views (Classic + Board via MachineBar, Roster via the bar).
 *
 * Four states per cell (computed by the pure buildWeekStripStates helper in
 * utils/runModeRendering.ts so the per-mode branch is unit-testable):
 *   • booked  — a covered WORKING day → solid sage.
 *   • non-working — a covered day that is a weekend OR a stat holiday on an
 *     ATTENDED booking → greyed + faint hatch (matches the capacity grid's
 *     weekend/holiday shading), so a span that runs across e.g. Canada Day
 *     doesn't light that day up as if it were worked.
 *   • run — Run Mode Phase C: a covered weekend/holiday on a CONTINUOUS (24/7)
 *     booking → filled in the continuous token. The specimen IS running that
 *     day, so the cell reads active, not closed.
 *   • off — not in the booking's span → faint label, no fill.
 */

import React from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { buildWeekStripStates } from '../../../../utils/runModeRendering';

// BST week is Saturday→Friday.
const DAY_LABELS = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'] as const;

export interface BookingWeekStripProps {
  /** ISO 'YYYY-MM-DD' — first covered day. */
  rangeStart: string;
  /** ISO 'YYYY-MM-DD' — last covered day. */
  rangeEnd: string;
  /**
   * ISO 'YYYY-MM-DD' stat-holiday dates. A covered day that lands on one of these (or on
   * a weekend) renders greyed/hatched rather than booked — the same non-working treatment
   * the capacity grid uses. Omit ⇒ only weekends are treated as non-working.
   */
  holidays?: string[];
  /**
   * Run Mode Phase C — 'continuous' renders covered weekend/holiday cells as
   * RUNNING (continuous token fill) instead of greyed non-work. Omit = attended.
   */
  runMode?: 'attended' | 'continuous' | null;
}

export const BookingWeekStrip: React.FC<BookingWeekStripProps> = ({ rangeStart, rangeEnd, holidays, runMode }) => {
  const theme = useTheme();
  // Stable dep key so the memo re-runs when the holiday list changes (array identity isn't).
  const holidayKey = holidays ? holidays.join(',') : '';
  const states = React.useMemo(
    () => buildWeekStripStates(rangeStart, rangeEnd, holidays, runMode),
    [rangeStart, rangeEnd, holidayKey, runMode], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ display: 'flex', gap: '2px', width: '100%' }} aria-hidden="true">
      {DAY_LABELS.map((lbl, i) => {
        const booked = states[i] === 'work';
        const nonwork = states[i] === 'nonwork';
        const running = states[i] === 'run';
        return (
          <Box
            key={lbl}
            sx={{
              flex: 1, textAlign: 'center', fontSize: '0.58rem', fontWeight: 700,
              py: '1px', borderRadius: '3px', lineHeight: 1.5,
              color: booked || running ? 'common.white' : 'text.disabled',
              backgroundColor: booked
                ? theme.schedule.employeeAlloc
                : running
                  ? alpha(theme.schedule.continuous, 0.85)
                  : nonwork
                    ? alpha(theme.palette.text.disabled, 0.1)
                    : 'transparent',
              // Non-working day inside an ATTENDED span: faint diagonal hatch so it
              // reads as blocked out (weekend / stat holiday), matching the capacity
              // grid. Continuous 'run' cells stay solid — the machine is running.
              ...(nonwork
                ? { backgroundImage: `repeating-linear-gradient(45deg, ${alpha(theme.palette.text.disabled, 0.22)} 0 2px, transparent 2px 4px)` }
                : {}),
            }}
          >
            {lbl}
          </Box>
        );
      })}
    </Box>
  );
};
